import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calculator, BookMarked, Timer, Activity, TrendingUp,
  BookOpen, FileText, PieChart, ArrowRight, Shield,
  Zap, Clock, AlertTriangle, Brain, Target, Award,
  ChevronRight, Sparkles, CheckCircle, BarChart3
} from "lucide-react";

const CSATHome = ({ isPro, setMode, isSI = false }) => {
  const navigate = useNavigate();
  
  const modeCards = [
    {
      id: "learn",
      title: "Learn Concepts",
      desc: "Notes, formulas & solved examples",
      icon: BookOpen,
      color: "from-blue-500 to-indigo-500",
      free: true,
      badge: "NEW"
    },
    {
      id: "universal-practice",
      title: "Practice MCQs",
      desc: "Prelims-standard MCQs • Easy to Advanced",
      icon: Zap,
      color: "from-emerald-500 to-teal-500",
      free: true,
      freemium: true,
      highlight: true
    },
    {
      id: "test-series",
      title: "Timed Test Series",
      desc: "Full 80-Q · 120-min CSAT simulation • CLARIAS behaviour report",
      icon: Timer,
      color: "from-indigo-500 to-purple-500",
      free: false,
      badge: "PRO",
      hidden: isSI
    }
  ];

  // SI-track users get the SI Mains Test Series (200-Q papers) instead of the
  // UPSC CSAT timed test.
  if (isSI) {
    modeCards.push({
      id: "si-test-series",
      title: "SI Mains Test Series",
      desc: "Full 200-Q · 180-min SI Mains papers • CLARIAS behaviour report",
      icon: Award,
      color: "from-blue-500 to-indigo-500",
      free: false,
      badge: "PRO"
    });
  }

  const visibleCards = modeCards.filter((m) => !m.hidden);

  // Exam-aware copy so SI-track users don't see CSAT-specific messaging.
  const copy = isSI ? {
    badgeSuite: "SI Aptitude Suite",
    tagline: "Arithmetic • Reasoning • Data Interpretation • 200-mark mastery",
    stat1: { value: "200", label: "Marks / Paper" },
    stat2: { value: "180m", label: "Duration" },
    principles: [
      { text: "No negative marking — attempt all", icon: CheckCircle, color: "from-emerald-500 to-teal-500" },
      { text: "Arithmetic is the biggest scorer", icon: Calculator, color: "from-blue-500 to-indigo-500" },
      { text: "Speed wins the 200-Q race", icon: Zap, color: "from-amber-500 to-orange-500" },
      { text: "Reasoning rewards practice", icon: Brain, color: "from-violet-500 to-purple-500" },
      { text: "DI needs calm, careful reading", icon: BarChart3, color: "from-sky-500 to-cyan-500" },
      { text: "Every mark counts — maximise", icon: TrendingUp, color: "from-rose-500 to-pink-500" },
    ],
    diffTitle: "What Makes SI Aptitude Suite Different",
    diffs: [
      { icon: Timer, title: "200-Q Mocks", desc: "Full 180-min papers", color: "from-blue-500 to-indigo-500" },
      { icon: Zap, title: "Speed Drills", desc: "Beat the clock", color: "from-amber-500 to-orange-500" },
      { icon: PieChart, title: "Sectional Analysis", desc: "Arith / Reason / DI", color: "from-sky-500 to-cyan-500" },
      { icon: Brain, title: "Behaviour Report", desc: "CLARIAS diagnostics", color: "from-violet-500 to-purple-500" },
      { icon: Target, title: "Weakness Map", desc: "Know where to focus", color: "from-rose-500 to-pink-500" },
    ],
    strategy: {
      good: { title: "Attempt Immediately", items: ["Basic arithmetic (30s type)", "Clear reasoning puzzles", "Simple DI reads", "Direct formula questions"] },
      later: { title: "Attempt Later", items: ["Multi-step arithmetic", "Complex reasoning chains", "Multi-set DI", "Lengthy puzzles"] },
      risk: { title: "Guess & Move On", subtitle: "(No negative marking!)", items: ["Never leave a blank", "Mark best-guess, revisit", "Don't over-invest time", "Bank the easy marks first"] },
    },
    proTitle: "Unlock PRO SI Features",
    proDesc: "Full 200-Q mock papers, CLARIAS behaviour report, and progress tracking.",
  } : {
    badgeSuite: "CSAT Suite",
    tagline: "Qualifying paper clarity • Accuracy-first approach • 66+ marks strategy",
    stat1: { value: "66+", label: "Target Score" },
    stat2: { value: "Pass", label: "Goal" },
    principles: [
      { text: "CSAT is qualifying, not scoring", icon: CheckCircle, color: "from-emerald-500 to-teal-500" },
      { text: "Accuracy > Attempts > Speed", icon: Zap, color: "from-amber-500 to-orange-500" },
      { text: "Elimination beats calculation", icon: Target, color: "from-blue-500 to-indigo-500" },
      { text: "RC = Safest scoring zone", icon: BookOpen, color: "from-sky-500 to-cyan-500" },
      { text: "Panic causes most failures", icon: Brain, color: "from-violet-500 to-purple-500" },
      { text: "Goal: CLEAR, not master", icon: Award, color: "from-rose-500 to-pink-500" },
    ],
    diffTitle: "What Makes CSAT Suite Different",
    diffs: [
      { icon: Target, title: "Elimination Logic", desc: "Reject 2 options first", color: "from-blue-500 to-indigo-500" },
      { icon: Clock, title: "Time Estimates", desc: "30s / 60s / 90s / Skip", color: "from-amber-500 to-orange-500" },
      { icon: AlertTriangle, title: "Trap Detection", desc: "Common PYQ traps flagged", color: "from-rose-500 to-pink-500" },
      { icon: Brain, title: "Panic Control", desc: "Mindset resets included", color: "from-violet-500 to-purple-500" },
      { icon: Shield, title: "Safety Score", desc: "Know where you stand", color: "from-emerald-500 to-teal-500" },
    ],
    strategy: {
      good: { title: "Attempt Immediately", items: ["RC comprehension questions", "Basic arithmetic (30s type)", "Clear logical puzzles", "Decision making (obvious)"] },
      later: { title: "Attempt Later", items: ["Medium DI (60s type)", "Complex reasoning", "Multi-step calculations", "Lengthy RC passages"] },
      risk: { title: "Skip Without Regret", subtitle: "", items: ["Complex sequences", "4+ variable DI", "Unclear language questions", "Time > 90s estimation"] },
    },
    proTitle: "Unlock PRO CSAT Features",
    proDesc: "Full mock tests, safety score predictor, and progress tracking.",
  };

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Premium Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-purple-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-10 space-y-10">
        
        {/* Premium Header */}
        <div className="glass-card rounded-2xl p-6 md:p-8 animate-fade-up">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3 mb-1">
                <Badge className={`px-3 py-1 ${
                  isPro 
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0' 
                    : 'bg-slate-100 text-slate-600 border-0'
                }`}>
                  {isPro ? (
                    <><Award className="w-3 h-3 mr-1" /> PRO</>
                  ) : 'FREE'}
                </Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                {copy.badgeSuite}
              </h1>
              <p className="text-slate-600">
                {copy.tagline}
              </p>
            </div>
            
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center px-4 py-2 bg-white/60 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{copy.stat1.value}</p>
                <p className="text-xs text-slate-500">{copy.stat1.label}</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/60 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">{copy.stat2.value}</p>
                <p className="text-xs text-slate-500">{copy.stat2.label}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Mode Cards */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-slate-900">
            Explore {copy.badgeSuite}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCards.map((mode, idx) => (
              <Card 
                key={mode.id}
                className={`card-interactive group border-0 overflow-hidden rounded-2xl shadow-sm hover:shadow-premium transition-all duration-300 bg-white ${
                  mode.highlight ? 'ring-2 ring-emerald-200' : ''
                }`}
                onClick={() => mode.external ? navigate('/strategy-dashboard') : setMode(mode.id)}
                style={{ animationDelay: `${(idx + 2) * 50}ms` }}
                data-testid={`csat-mode-${mode.id}`}
              >
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${mode.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <mode.icon className="w-11 h-11 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex items-center gap-2">
                      {mode.badge && (
                        <Badge className={`border-0 text-xs ${mode.free ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white animate-pulse" : "bg-gradient-to-r from-indigo-500 to-purple-500 text-white"}`}>
                          {mode.badge}
                        </Badge>
                      )}
                      {mode.free ? (
                        <Badge variant="outline" className="text-xs border-emerald-200 bg-emerald-50 text-emerald-700">
                          {mode.freemium && !isPro ? "FREE · 10 Q" : "FREE"}
                        </Badge>
                      ) : !mode.badge && (
                        <Badge variant="outline" className="text-xs border-indigo-200 bg-indigo-50 text-indigo-700">
                          PRO
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                      {mode.title}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                      {mode.desc}
                    </p>
                    {mode.freemium && !isPro && (
                      <p className="text-[11px] font-medium text-amber-600">Free: 10 questions/set · PRO unlocks full sets</p>
                    )}
                  </div>
                  
                  <div className="flex items-center text-xs text-slate-400 group-hover:text-indigo-500 transition-colors pt-1">
                    <span className="font-medium">Open</span>
                    <ChevronRight className="w-4 h-4 ml-0.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Feature Grid */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            {copy.diffTitle}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {copy.diffs.map((feature, idx) => (
              <div key={idx} className="text-center p-4 bg-white rounded-xl border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mx-auto mb-3 shadow-sm`}>
                  <feature.icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <h4 className="text-sm font-semibold text-slate-900">{feature.title}</h4>
                <p className="text-xs text-slate-500 mt-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Question Selection Strategy */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-500" />
            Question Selection Strategy
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800">{copy.strategy.good.title}</span>
              </div>
              <ul className="space-y-1 text-sm text-emerald-700">
                {copy.strategy.good.items.map((it, i) => <li key={i}>• {it}</li>)}
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-800">{copy.strategy.later.title}</span>
              </div>
              <ul className="space-y-1 text-sm text-amber-700">
                {copy.strategy.later.items.map((it, i) => <li key={i}>• {it}</li>)}
              </ul>
            </div>
            <div className={`${isSI ? "bg-sky-50 border-sky-200" : "bg-red-50 border-red-200"} border rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-3">
                {isSI ? <Zap className="w-5 h-5 text-sky-600" /> : <AlertTriangle className="w-5 h-5 text-red-600" />}
                <span className={`font-semibold ${isSI ? "text-sky-800" : "text-red-800"}`}>{copy.strategy.risk.title}</span>
                {copy.strategy.risk.subtitle && <span className="text-xs text-sky-600 font-medium">{copy.strategy.risk.subtitle}</span>}
              </div>
              <ul className={`space-y-1 text-sm ${isSI ? "text-sky-700" : "text-red-700"}`}>
                {copy.strategy.risk.items.map((it, i) => <li key={i}>• {it}</li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* PRO CTA for Free Users */}
        {!isPro && (
          <div className="rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 p-6 relative overflow-hidden animate-gradient" style={{ backgroundSize: '200% 200%' }}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
              
              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-white">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <h3 className="font-bold text-lg">{copy.proTitle}</h3>
                  </div>
                  <p className="text-white/80 text-sm">
                    {copy.proDesc}
                  </p>
                </div>
                <Button 
                  className="bg-white text-indigo-600 hover:bg-slate-100 rounded-full px-8 py-6 font-semibold shadow-lg btn-premium"
                  onClick={() => window.location.href = '/upgrade'}
                >
                  Upgrade to PRO
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATHome;
